/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import {
	SideNavPanelSplitter,
	SideNav,
	SideNavBody,
	SideNavFooter,
	SideNavHeader,
} from '@atlaskit/navigation-system/layout/side-nav';
import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';

import { messages } from './messages';
import type { SideNavigationLayoutProps } from './types';

const footerStyles = cssMap({
	/**
	 * Hide the footer wrapper (and the SideNavFooter border it contains) when the only
	 * content inside is an empty Post Office placement sentinel.
	 *
	 * The sentinel is a `<div data-empty-side-nav-placement />` emitted by a Post Office
	 * placement component (e.g. the Jira side-nav placement) when it has no message to
	 * show. When the sentinel is the only child inside SideNavFooter, there is nothing
	 * meaningful for the user to see, so we hide the entire footer including its border.
	 *
	 * Only placements that explicitly emit this attribute participate in this behaviour;
	 * other bottomElem content are unaffected.
	 *
	 * Browser support: `:has()` requires Chrome 105+, Safari 15.4+, Firefox 121+.
	 */
	hideWhenEmpty: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has([data-empty-side-nav-placement]:only-child)': {
			display: 'none',
		},
	},
});

/**
 * SideNavigationLayout - A layout component that provides a side navigation structure.
 *
 * This component is designed to be used as a wrapper for the side navigation system,
 * providing a consistent layout with header, content, and footer sections.
 * This is a controlled and will not manage any state internally.
 *
 * If you need state management, analytics, or supporting Drag and Drop
 * please consider using the `GlobalSideNavigation` component instead.
 *
 * Props:
 * - bottomElem: Element to render at the bottom of the side navigation.
 * - children: Main content of the side navigation layout.
 * - defaultCollapsed: Whether the side nav is collapsed by default.
 * - defaultWidth: Default width of the side nav.
 * - onCollapse: Callback when the side nav is collapsed.
 * - onExpand: Callback when the side nav is expanded.
 * - onResizeEnd: Callback when the resize of the side nav ends.
 * - onResizeStart: Callback when the resize of the side nav starts.
 * - postMainElem: Element to render after the main content.
 * - preMainElem: Element to render before the main content.
 * - sideNavContentRef: Ref for the SideNavBody component.
 * - testId: Optional test identifier for testing purposes.
 *
 */
export function SideNavigationLayout({
	id,
	preMainElem,
	postMainElem,
	bottomElem,
	children,
	testId,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	onResizeStart,
	onResizeEnd,
	defaultWidth,
	defaultCollapsed,
	sideNavContentRef,
}: SideNavigationLayoutProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	// only render the top level spacer if the there is content in the children and postMainElem
	const shouldRenderTopLevelSpacer = Boolean(children && postMainElem);

	return (
		<SideNav
			id={id}
			testId={testId}
			onExpand={onExpand}
			onCollapse={onCollapse}
			onPeekStart={onPeekStart}
			onPeekEnd={onPeekEnd}
			defaultWidth={defaultWidth}
			defaultCollapsed={defaultCollapsed}
		>
			{preMainElem && <SideNavHeader>{preMainElem}</SideNavHeader>}
			<SideNavBody ref={sideNavContentRef}>
				{children}
				{shouldRenderTopLevelSpacer ? <TopLevelSpacer /> : null}
				{postMainElem}
			</SideNavBody>
			<SideNavPanelSplitter
				onResizeEnd={onResizeEnd}
				onResizeStart={onResizeStart}
				label={formatMessage(messages.panelSplitterLabel)}
				tooltipContent={formatMessage(messages.panelSplitterTooltipContent)}
			/>
			{bottomElem && (
				<div css={footerStyles.hideWhenEmpty}>
					<SideNavFooter>{bottomElem}</SideNavFooter>
				</div>
			)}
		</SideNav>
	);
}
